import React from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Oval } from 'react-loader-spinner';
import { BsLock, BsUnlock } from 'react-icons/bs';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  getTeamSafetySummary,
  lockTeamSafetyPeriod,
  unlockTeamSafetyPeriod,
} from '../../driversSlice';
import { EditInclusionModal } from './EditInclusionModal';

import './TeamSafetyScoreCard.scss';

interface Props {
  offset: number;
}

const fmtDelta = (d: number): string => `${d > 0 ? '+' : ''}${d}`;

export function TeamSafetyScoreCard({ offset }: Props) {
  const dispatch = useAppDispatch();
  const [editOpen, setEditOpen] = React.useState(false);

  const summary = useAppSelector(state => state.drivers.teamSafetySummary);
  const api = useAppSelector(state => state.drivers.getTeamSafetySummary);
  const mutation = useAppSelector(state => state.drivers.teamSafetyMutation);

  React.useEffect(() => {
    dispatch(getTeamSafetySummary(offset));
  }, [dispatch, offset]);

  const refetch = React.useCallback(() => {
    dispatch(getTeamSafetySummary(offset));
  }, [dispatch, offset]);

  // Card is scoped to whichever period the parent tab is showing. If the fetched
  // summary is for a different period (mid-navigation), don't act on stale data.
  const payPeriodId = summary?.payPeriod?.id;

  const onLock = () => {
    if (!payPeriodId) return;
    const ok = window.confirm(
      'Lock this pay period? This freezes the team score and membership for ' +
      'payroll — exclusions can no longer be edited until it is unlocked.'
    );
    if (!ok) return;
    dispatch(lockTeamSafetyPeriod(payPeriodId))
      .unwrap()
      .then(() => { toast.success('Pay period locked'); refetch(); })
      .catch(() => toast.error('Could not lock this pay period'));
  };

  const onUnlock = () => {
    if (!payPeriodId) return;
    const ok = window.confirm(
      'Unlock this pay period? The score will recompute from live data and ' +
      'become editable again.'
    );
    if (!ok) return;
    dispatch(unlockTeamSafetyPeriod(payPeriodId))
      .unwrap()
      .then(() => { toast.success('Pay period unlocked'); refetch(); })
      .catch(() => toast.error('Could not unlock this pay period'));
  };

  // First load: show a slim placeholder so the tab doesn't jump.
  if (!summary && api?.loading) {
    return (
      <div className='team-safety-card team-safety-card--loading'>
        <Oval color='#131313' height={22} width={22} secondaryColor='#FBD20A' />
      </div>
    );
  }
  if (!summary) return null;

  const {
    teamScore, simpleAverage, deltaVsPrev, driverCount,
    provisional, locked, lockedAt, lockedBy,
    autoExcluded, manuallyExcluded,
  } = summary;

  const mutating = !!mutation?.loading;
  const canLock = !locked && !provisional && driverCount > 0;

  const status = locked
    ? { cls: 'locked', label: 'Locked' }
    : provisional
      ? { cls: 'provisional', label: 'Provisional' }
      : { cls: 'settled', label: 'Settled — ready to lock' };

  return (
    <div className='team-safety-card'>
      <div className='tsc-main'>
        <div className='tsc-score-block'>
          <div className='tsc-heading-row'>
            <span className='tsc-title'>Team Safety Score</span>
            <span className={`tsc-badge tsc-badge--${status.cls}`}>
              {locked && <BsLock />} {status.label}
            </span>
          </div>
          <div className='tsc-score-row'>
            <span className='tsc-score'>
              {teamScore != null ? teamScore : 'No scored trips'}
            </span>
            {deltaVsPrev != null && (
              <span className={`tsc-delta ${deltaVsPrev >= 0 ? 'up' : 'down'}`}>
                {fmtDelta(deltaVsPrev)} vs last period
              </span>
            )}
          </div>
          <div className='tsc-subrow'>
            {simpleAverage != null && (
              <span>simple avg {simpleAverage}</span>
            )}
            <span>
              {driverCount} counted · {autoExcluded.length} not driving · {manuallyExcluded.length} excluded
            </span>
          </div>
          {locked && lockedBy && (
            <div className='tsc-locked-meta'>
              Locked{lockedAt ? ` ${moment(lockedAt).format('MMM D, YYYY')}` : ''}
            </div>
          )}
        </div>

        <div className='tsc-actions'>
          <button
            className='tsc-btn tsc-btn--secondary'
            onClick={() => setEditOpen(true)}
            disabled={locked || mutating}
            title={locked ? 'Unlock the period to edit who counts' : undefined}
          >
            Edit who counts
          </button>
          {locked ? (
            <button
              className='tsc-btn tsc-btn--ghost'
              onClick={onUnlock}
              disabled={mutating}
            >
              <BsUnlock /> Unlock
            </button>
          ) : (
            <button
              className='tsc-btn tsc-btn--primary'
              onClick={onLock}
              disabled={!canLock || mutating}
              title={
                provisional
                  ? 'Wait until the period has settled before locking'
                  : driverCount === 0
                    ? 'No drivers to lock'
                    : undefined
              }
            >
              <BsLock /> Lock period
            </button>
          )}
          {mutating && (
            <Oval color='#131313' height={18} width={18} secondaryColor='#FBD20A' />
          )}
        </div>
      </div>

      <EditInclusionModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        summary={summary}
        onChanged={refetch}
      />
    </div>
  );
}
